import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore, activeEnv, API_BASE_BY_ENV, setApiBase, setOnUnauthorized } from '@wordpuppi/shared';
import { Loader2 } from 'lucide-react';

import { routeTree } from './routeTree.gen';
import { isAccountScopedQuery, useActiveAccountStore } from './lib/activeAccount';
import './index.css';

// The desktop shell's iframe (and any file-based loader) opens us as
// `/index.html#env=...`, but the router serves at root — normalize the path
// before createRouter reads location, keeping search + env hash intact.
if (window.location.pathname.endsWith('/index.html')) {
  window.history.replaceState(
    null,
    '',
    window.location.pathname.slice(0, -'index.html'.length) +
      window.location.search +
      window.location.hash,
  );
}

// AB#425: when the desktop shell launches us at `.../#env=qa`, point the API client
// at that backend. Runs before first render (and thus before any request). The auth
// persist key is namespaced separately at import time inside @wordpuppi/shared.
if (activeEnv) {
  setApiBase(API_BASE_BY_ENV[activeEnv]);
}

// #623: a previous session's selected-site badge (desktop shell titlebar) must
// not survive a relaunch/reload — the site layout republishes it on mount.
localStorage.removeItem('wpp-current-site');

// Keyboard actions never animate (docs/style/animation.md): for a beat after Escape,
// index.css zeroes the exit transition of whatever popup or dialog it closes.
let keyDismissTimer: ReturnType<typeof setTimeout> | undefined;
window.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.documentElement.setAttribute('data-key-dismiss', '');
  clearTimeout(keyDismissTimer);
  keyDismissTimer = setTimeout(() => document.documentElement.removeAttribute('data-key-dismiss'), 400);
}, true);

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Create router. AB#447: SPA serves at the site root in every environment.
const router = createRouter({
  routeTree,
  basepath: '/',
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// AB#492: on any 401 (expired/invalid token), clear the session and bounce to
// /login — parity with wpp-mobile. The shared authStore registers a clear-only
// handler at import; this overrides it to also redirect (single-slot handler).
setOnUnauthorized(() => {
  useAuthStore.getState().clear();
  if (window.location.pathname !== '/login') {
    void router.navigate({ to: '/login' });
  }
});

// Desktop stale-account fix: TanStack query keys aren't user-scoped, so a prior
// session's /api/accounts + /api/sites bleed into the next user after a
// login/switch on the SAME page — most visibly in the persisted desktop webview,
// where an old (still-valid) QA JWT could log in as a different account and the
// Team page kept showing the previous owner. Dropping the whole cache on any
// change of the signed-in user's pid is the single chokepoint every auth path
// (email/password, social, logout, boot hydration) routes through.
let lastAuthPid: string | null = useAuthStore.getState().user?.pid ?? null;
useAuthStore.subscribe((state) => {
  const pid = state.user?.pid ?? null;
  if (pid !== lastAuthPid) {
    lastAuthPid = pid;
    // resetQueries, NOT clear(): clear() removes actively-observed queries
    // without notifying their observers, so any query in flight at the moment
    // of a login/switch (e.g. /sites mounted before hydration finished) hangs
    // in pending forever — skeletons until a manual refresh. resetQueries
    // drops all cached data the same way but refetches active observers.
    void queryClient.resetQueries();
  }
});

// Account switch (AB#1014): same idea one level down. Sites, members, keys and
// everything under them belong to an account, so a switch drops them all and
// refetches what is on screen — the switched-to account opens fresh, never on
// the previous account's cached rows. The account LIST is the user's, not the
// account's, so it is kept: resetting it would blank the menu mid-switch.
let lastAccountPid = useActiveAccountStore.getState().pid;
useActiveAccountStore.subscribe(({ pid }) => {
  if (pid !== lastAccountPid) {
    lastAccountPid = pid;
    void queryClient.resetQueries({ predicate: isAccountScopedQuery });
  }
});

/**
 * Validate any persisted JWT against /api/auth/current before rendering the app,
 * so a stale/expired token lands on /login instead of flashing the authed shell.
 * checkAuth() short-circuits (no network) when no token is persisted, so public
 * auth routes (login/register/verify/forgot/reset) render immediately for strangers.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let active = true;
    useAuthStore
      .getState()
      .checkAuth()
      .finally(() => {
        if (active) setChecked(true);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}

// Render app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthGate>
          <RouterProvider router={router} />
        </AuthGate>
      </QueryClientProvider>
    </StrictMode>,
  );
}
